import React from 'react';
import PropTypes from 'prop-types';
import { getTagActivities } from '../../../api/api-env';
import { find, get } from 'lodash';
import classnames from 'classnames';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import { injectIntl } from 'react-intl';
import Cookie from 'react-cookie';
import { toTitleCase } from '../../../lib/utils';

const withActivityDropdownHoc = (Component) => {
  class HOC extends React.Component {
    constructor (props) {
      super(props);
      this.initialState = {
        activity: '',
        activityId: '',
        activities: [],
        activitiesList: [],
        showModal: false
      };

      this.state = Object.assign({}, this.initialState);

      this.onFocusActivity = this.onFocusActivity.bind(this);
      this.onBlurActivity = this.onBlurActivity.bind(this);
      this.onChangeActivity = this.onChangeActivity.bind(this);
      this.onClickActivity = this.onClickActivity.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.checkForExactMatch = this.checkForExactMatch.bind(this);
      this.matchActivity = this.matchActivity.bind(this);
    }

    componentDidMount () {
      const { bookingActivity, excludeCustomActivities, getValueFromInput } = this.props;
      let activityId = bookingActivity || '';
      const locale = SharedIntlProviderHOC.getLocale();
      const translations = SharedIntlProviderHOC.getTranslations();
      getTagActivities(locale, res => {
        const activities = res.map((act, index) => {
          const titleCaseActivity = toTitleCase(act);
          return { dropdown: titleCaseActivity, id: titleCaseActivity, index };
        });
        const formattedActivity = toTitleCase(activityId);
        const match = this.checkForExactMatch(activities, formattedActivity);

        if (excludeCustomActivities && !match) {
          PSSessions.Cookies.expire('saved-activity', formattedActivity);
          if ('function' === typeof(getValueFromInput)) {
            getValueFromInput({ activity: '' });
          }
        }

        this.setState({
          activity: !excludeCustomActivities || match ? formattedActivity : '',
          activityId: !excludeCustomActivities || match ? formattedActivity : '',
          activities: activities,
          activitiesList: []
        });
      });

    }

    checkForExactMatch (activities, activityId) {
      return find(activities, (activity) => {
        return activityId === activity.id;
      });
    }

    matchActivity (activities, activityId) {
      // tries to find an exact match of activities based on ID
      // if none is found, then we use sifter to find a close approximation
      let match = {};
      const exactMatch = this.checkForExactMatch(activities, activityId);

      if (exactMatch) {
        match = exactMatch
      }
      else {
        const sifter = new Sifter(activities);
        const fuzzyMatch = (sifter.search(activityId,
          { fields: ['id'],
            limit: 1
          })).items || [];
        const matchSifterId = _.get(fuzzyMatch, '[0].id');
        match = _.get(activities, `[${matchSifterId}]`)
      }

      return match;
    }

    onChangeActivity (e) {
      const activity = e.currentTarget.value;
      this.setState({
        activity,
        activityId: activity
      }, () => {
        if (activity) return this.getActivitySuggestions(activity);
        return this.setState({
          activityId: '',
          activitiesList: []
        });
      });
    }

    onFocusActivity (e) {
      const { activity } = this.state;
      if (e) e.currentTarget.select();
      if (activity) return this.getActivitySuggestions(activity);
      return null;
    }

    onClickActivity (e, closeDropdownOrModal) {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }

      const activityId = _.get(e, 'currentTarget.dataset.value', '');
      const filteredActivity = _.filter(this.state.activities, { id: activityId })[0] || {};
      const activity = filteredActivity.dropdown || activityId;
      if (_.isFunction(closeDropdownOrModal)) closeDropdownOrModal();
      setTimeout(() => {
        this.setState({
          activity,
          activityId
        });
        PSSessions.Cookies.set('saved-activity', activityId, Date.now() + 172800000);

        if ('function' === typeof(this.props.getValueFromInput)) this.props.getValueFromInput({ activity: activityId });
      }, 200);
    }

    onBlurActivity (e) {
      if (e) e.stopPropagation();
      const { activity, activityId, activities } = this.state;
      const { excludeCustomActivities } = this.props;
      const inputActivity = activityId || activity;
      const formattedActivity = toTitleCase(inputActivity);

      const match = this.checkForExactMatch(activities, formattedActivity);

      if (!excludeCustomActivities || match) {
        PSSessions.Cookies.set('saved-activity', formattedActivity, Date.now() + 172800000);
        if ('function' === typeof(this.props.getValueFromInput)) this.props.getValueFromInput({ activity: formattedActivity });
      } else if (!match) {
        if ('function' === typeof(this.props.getValueFromInput)) {
          PSSessions.Cookies.expire('saved-activity');
          this.props.getValueFromInput({ activity: '' });
        }
      }
    }

    onKeyDown (e) {
      const firstOnList = document.getElementById('activity-list-0');
      if (40 === e.keyCode && firstOnList) {
        // If user presses the down arrow,
        // we select the first item from the dropdown
        e.preventDefault();
        firstOnList.focus();
      } else if (13 === e.keyCode && firstOnList) {
        // If user hits enter, we select the first item from the dropdown,
        // and blur out of the input field
        this.inputOverlay.closeDropdown();
        e.target.blur();
      }
    }

    getActivitySuggestions (input) {
      const { Sifter } = window;
      const { activities } = this.state;
      const siftedActivity = new Sifter(activities).search(input, { fields: ['dropdown'], limit: 5 });
      const activitiesList = !_.isEmpty(siftedActivity) ? _.map(siftedActivity.items, (item) => {
        return activities[item.id];
      }) : [];
      this.setState({ activitiesList });
    }

    render () {
      let placeholder;
      switch (this.props.listingUseType) {
      case 'Off-Site':
        placeholder = this.props.intl.formatMessage({ id: 'booking_request.activity_placeholder.offsite', defaultMessage: 'e.g. Meeting, Workshop, Retreat...' });
        break;
      case 'Production':
        placeholder = this.props.intl.formatMessage({ id: 'booking_request.activity_placeholder.production', defaultMessage: 'e.g. Photo Shoot, Video Shoot....' });
        break;
      case 'Event':
        placeholder = this.props.intl.formatMessage({ id: 'booking_request.activity_placeholder.event', defaultMessage: 'e.g. Birthday, Office Party, Shower...' });
        break;
      default:
        placeholder = this.props.intl.formatMessage({ id: 'booking_request.activity_placeholder.default', defaultMessage: 'Enter your specific activity' });
      }
      const name = get(this.props, 'name', '');
      const error = get(this.props, 'meta.error', null);
      const submitFailed = get(this.props, 'meta.submitFailed', null);
      const showError = error && submitFailed;
      return (
        <div>
          <Component
            name={name}
            activity={this.state.activity}
            activityId={this.state.activityId}
            activitiesList={this.state.activitiesList}
            inputRef={this.props.inputRef}
            inputOverlayRef={ref => { this.inputOverlay = ref; }}
            placeholder={placeholder}
            onFocusActivity={this.onFocusActivity}
            onBlurActivity={this.onBlurActivity}
            onClickActivity={this.onClickActivity}
            onChangeActivity={this.onChangeActivity}
            onKeyDown={this.onKeyDown}
            inputClassName={classnames(`${this.props.inputClassName}`, { error: showError })}
            hideModalLink
            hideErrorDropdown
            hideDefaultHighlight
            inputAttributes={{
              maxLength: '32',
              name: 'tags'
            }}
          />
          { showError && <p className="error text-danger text-small">{error}</p> }
        </div>
      );
    }
  }
  HOC.propTypes = {
    bookingActivity: PropTypes.string,
    listingUseType: PropTypes.string,
    inputClassName: PropTypes.string,
    getValueFromInput: PropTypes.func,
    excludeCustomActivities: PropTypes.bool,
    inputRef: PropTypes.func // required if you are using custom-input-hoc
  };
  return injectIntl(HOC);
};

export default withActivityDropdownHoc;
